<script setup lang="ts">
/** Registered Address Service Agreement + Annexure B */
import { computed, onMounted } from 'vue';
import CddVarField from '../incorporation-pre/cdd/CddVarField.vue';
import { useCddFill } from '../incorporation-pre/cdd/useCddFill';
import SgPostSigners from './SgPostSigners.vue';
import { SG_POST_SIGNER_KEYS } from './sgPostSignersContext';
import './allotDoc.css';

type Mail = 'keep_for_collection' | 'forward_to_address' | 'scan_and_email';
const MAIL_OPTS: Mail[] = ['keep_for_collection', 'forward_to_address', 'scan_and_email'];

const fill = useCddFill();

function isYes(key: string): boolean {
  const v = fill.values[key];
  if (v === true) return true;
  const s = String(v ?? '').trim().toLowerCase();
  return s === 'yes' || s === 'true' || s === '1' || s === 'y' || s === 'checked';
}

function mark(on: boolean) {
  return on ? '☑' : '☐';
}

/** Word: [[if service_registered_address]]… */
function toggleService() {
  fill.setValue('service_registered_address', isYes('service_registered_address') ? 'no' : 'yes');
}

/** Word: [[if mail_handling == "…"]]… */
const mailHandling = computed({
  get(): Mail {
    const v = String(fill.values.mail_handling ?? '').trim();
    return (MAIL_OPTS.includes(v as Mail) ? v : 'scan_and_email') as Mail;
  },
  set(v: Mail) {
    fill.setValue('mail_handling', v);
  },
});

function mailMark(v: Mail) {
  return mailHandling.value === v ? '☑' : '☐';
}

onMounted(() => {
  if (!String(fill.values.service_registered_address ?? '').trim()) {
    fill.setValue('service_registered_address', 'yes');
  }
  if (!String(fill.values.mail_handling ?? '').trim()) {
    fill.setValue('mail_handling', 'scan_and_email');
  }
});
</script>

<template>
  <div class="allot-doc">
    <p class="doc-p">Date: <CddVarField field-key="agreement_date" label="Agreement Date" /></p>
    <p class="doc-p">
      To:<br />
      <CddVarField field-key="company_name" label="Company Name" /><br />
      <CddVarField field-key="company_registered_address" label="Company Registered Address" /><br />
      Attention: The Board of Directors
    </p>
    <p class="doc-p">Dear Sir / Madam,</p>
    <p class="doc-lbl-title">Subject: Registered Address Office Service Agreement</p>

    <p class="doc-sec-title" style="text-align:left">1. INSTRUCTIONS</p>
    <p class="doc-p">
      The Service Provider (<CddVarField field-key="csp_name" label="CSP Name" />) will act on instructions
      provided by the Company's board of directors or such other authorised person on behalf of the Company,
      and the Service Provider requires such instructions to be made in writing if they vary from the
      standing instructions mentioned in Annexure B. Any changes in the officers and the contact details of
      the Company should be communicated to the Service Provider immediately.
    </p>

    <p class="doc-sec-title" style="text-align:left">2. SCOPE OF SERVICES</p>
    <p class="doc-p">
      The Service Provider offers registered address and virtual office services on a per-need basis as follows:
    </p>
    <p class="doc-lbl-title">Registered Address and Mail Handling</p>
    <p class="doc-p">
      The Service Provider provides a prestigious address to comply with the statutory requirement to have a
      registered business address for the registration of the Company. The address can further be used as the
      Company's correspondence address on business cards, letterheads, or any other official documents. Any
      mail or document delivered to the Service Provider's address for the Company's account will be reliably
      received and handled according to the Company's instructions.
    </p>
    <ul class="sg-bullets">
      <li>Provision of the Service Provider's address as the Company's registered address.</li>
      <li>E-mail notification of the receipt of mail or documents addressed to the Company.</li>
    </ul>

    <p class="doc-sec-title" style="text-align:left">3. NOTICES</p>
    <p class="doc-p">
      Service instructions or changes of instructions, including the termination notice, contact information,
      and other company particulars shall be communicated to the Service Provider in writing through an email
      directed to <CddVarField field-key="csp_email" label="CSP Email" />.
    </p>

    <p class="doc-sec-title" style="text-align:left">4. FEES AND BILLING PROCEDURE</p>
    <p class="doc-p">
      The Service Provider's professional fees of
      <CddVarField field-key="currency_symbol" label="Currency" />
      <CddVarField field-key="registered_address_fee" label="Registered Address Fee" />
      are payable in advance on a yearly basis. The Service Provider reserves the right to amend the service
      fees subject to a 30-day notice in writing by electronic means or by publication on the Service
      Provider's website.
    </p>
    <p class="doc-p">
      Ad-hoc services, such as scanning or mail forwarding, are billed separately. The scanning service fee is
      <CddVarField field-key="currency_symbol" label="Currency" />
      <CddVarField field-key="scanning_fee_first_page" label="Scan Fee (1st page)" />
      for the first page and
      <CddVarField field-key="currency_symbol" label="Currency" />
      <CddVarField field-key="scanning_fee_subsequent_page" label="Scan Fee (subsequent)" />
      for each subsequent page per document. Mail forwarding is at cost, as per forwarder's quotation.
    </p>

    <p class="doc-sec-title" style="text-align:left">5. EXPENSES</p>
    <p class="doc-p">
      The fees for all of the Service Provider's services are exclusive of expenses and other costs that may
      be incurred in connection with the performance of the service. The Service Provider requires payment in
      advance for out-of-pocket expenses such as mail forwarding.
    </p>

    <p class="doc-sec-title" style="text-align:left">6. TERMINATION</p>
    <p class="doc-p">
      Either Party may terminate this Agreement upon providing
      <CddVarField field-key="termination_notice_days" label="Termination Notice (Days)" />
      days' written notice of its intention to terminate.
    </p>

    <p class="doc-sec-title" style="text-align:left">7. SERVICE CONFIRMATION</p>
    <p class="doc-p">
      We, <CddVarField field-key="company_name" label="Company Name" />, confirm our agreement to the terms
      of engagement set out herein and select your standard service(s) as follows:
    </p>
    <table class="cdd-tbl">
      <thead><tr><th>Service</th><th>Select</th></tr></thead>
      <tbody>
        <tr>
          <td>Registered Address Service</td>
          <td><span class="doc-toggle" @click="toggleService">{{ mark(isYes('service_registered_address')) }}</span></td>
        </tr>
      </tbody>
    </table>

    <p class="doc-lbl-title">For and on behalf of <CddVarField field-key="csp_name" label="CSP Name" /></p>
    <p class="doc-p">
      Name: <CddVarField field-key="csp_signatory_name" label="CSP Signatory" /><br />
      Designation: <CddVarField field-key="csp_signatory_title" label="CSP Title" /><br />
      Date: <CddVarField field-key="agreement_date" label="Agreement Date" />
    </p>

    <p class="doc-p" style="margin-top:12px">We accept the terms of this service agreement.</p>
    <p class="doc-lbl-title">For and on behalf of <CddVarField field-key="company_name" label="Company Name" /></p>
    <SgPostSigners :var-key="SG_POST_SIGNER_KEYS.regAddr" board-label="COMPANY & CSP SIGNERS" />
    <p class="doc-p">
      Name: <CddVarField field-key="company_signatory_name" label="Company Signatory" /><br />
      Designation: <CddVarField field-key="company_signatory_title" label="Company Title" /><br />
      Date: <CddVarField field-key="company_signature_date" label="Company Signature Date" />
    </p>

    <div class="page-div">— Annexure B —</div>
    <div class="doc-head">
      <div class="doc-lbl-title" style="text-align:center">ANNEXURE B</div>
      <div class="doc-meta">Standing Instructions for Registered Address and Virtual Office Services</div>
    </div>

    <table class="cdd-tbl">
      <tbody>
        <tr><td class="cdd-lcell">Company Name</td><td><CddVarField field-key="company_name" label="Company Name" /></td></tr>
        <tr><td class="cdd-lcell">Company Registration No.</td><td><CddVarField field-key="company_registration_no" label="Registration No." /></td></tr>
        <tr><td class="cdd-lcell">Date</td><td><CddVarField field-key="agreement_date" label="Agreement Date" /></td></tr>
        <tr><td class="cdd-lcell">Office Location</td><td><CddVarField field-key="csp_full_address" label="CSP Full Address" /></td></tr>
      </tbody>
    </table>

    <p class="doc-lbl-title">Mail Handling</p>
    <p class="doc-p">Please select the preferred mail handling option:</p>
    <p class="doc-p">
      <span class="doc-toggle" @click="mailHandling = 'keep_for_collection'">{{ mailMark('keep_for_collection') }} Keep for collection</span>
    </p>
    <p class="doc-p">
      <span class="doc-toggle" @click="mailHandling = 'forward_to_address'">{{ mailMark('forward_to_address') }} Forward to the below address</span>
    </p>
    <p class="doc-p">
      <span class="doc-toggle" @click="mailHandling = 'scan_and_email'">{{ mailMark('scan_and_email') }} Scan &amp; email</span>
    </p>

    <p class="doc-lbl-title">Contact Details for Mail Notification</p>
    <table class="cdd-tbl">
      <tbody>
        <tr><td class="cdd-lcell">Contact Name</td><td><CddVarField field-key="notification_contact_name" label="Notification Contact" /></td></tr>
        <tr><td class="cdd-lcell">Email Address</td><td><CddVarField field-key="notification_email" label="Notification Email" /></td></tr>
        <tr><td class="cdd-lcell">Contact No.</td><td><CddVarField field-key="notification_contact_no" label="Notification Contact No." /></td></tr>
        <tr v-if="mailHandling === 'forward_to_address'">
          <td class="cdd-lcell">Correspondence Address for Mail Forwarding</td>
          <td><CddVarField field-key="forwarding_address" label="Forwarding Address" /></td>
        </tr>
      </tbody>
    </table>
  </div>
</template>

<style scoped>
.sg-bullets { margin: 0 0 10px; padding-left: 22px; font-size: 12.5px; line-height: 1.55; }
</style>
